import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import { Calendar, User, Tag, ArrowLeft, ArrowRight, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://redballxpress.us';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// ==================== BLOG LISTING ====================
export const BlogListPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append('category', filter);
      params.append('limit', '20');
      const res = await axios.get(`${API}/blog?${params.toString()}`);
      let items = res.data.articles || [];
      if (search.trim()) {
        const q = search.toLowerCase();
        items = items.filter(a =>
          a.title.toLowerCase().includes(q) ||
          (a.excerpt || '').toLowerCase().includes(q) ||
          (a.keywords || '').toLowerCase().includes(q)
        );
      }
      setArticles(items);
      setTotal(res.data.total || 0);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    axios.get(`${API}/blog/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  return (
    <div data-testid="blog-list-page">
      <Helmet>
        <title>Freight & Logistics Blog | Red Ball Express</title>
        <meta name="description" content="Expert insights on freight shipping, trucking industry trends, logistics tips, and supply chain strategies." />
      </Helmet>

      {/* Hero */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-red-500 text-xs font-bold uppercase tracking-[0.2em]">Industry Insights</span>
          <h1 className="text-4xl sm:text-5xl font-black text-white mt-3 mb-4">Freight & Logistics Blog</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Expert tips, industry news, and shipping strategies from the Red Ball Express team.
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
                data-testid="blog-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('')}
                className={`px-4 py-2 text-xs font-bold uppercase border transition-colors ${!filter ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
              >
                All
              </button>
              {categories.map(c => (
                <button
                  key={c.name}
                  onClick={() => setFilter(c.name)}
                  className={`px-4 py-2 text-xs font-bold uppercase border transition-colors ${filter === c.name ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
                >
                  {c.name} ({c.count})
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="text-center py-20 text-zinc-400">Loading articles...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-zinc-400 text-lg">No articles found</p>
              <p className="text-zinc-400 text-sm mt-2">Check back soon for new content.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map(article => (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className="group bg-white border border-zinc-200 hover:border-zinc-300 overflow-hidden transition-all hover:shadow-lg"
                  data-testid={`article-card-${article.slug}`}
                >
                  {article.featured_image && (
                    <div className="aspect-video overflow-hidden bg-zinc-100">
                      <img
                        src={article.featured_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {article.category && (
                      <span className="text-xs font-bold uppercase text-red-600 tracking-wide">{article.category}</span>
                    )}
                    <h2 className="text-lg font-black text-zinc-900 mt-1 mb-2 group-hover:text-red-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-zinc-500 line-clamp-3 mb-4">{article.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      {article.published_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(article.published_at)}
                        </span>
                      )}
                      {article.author_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {article.author_name}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black text-white mb-3">Need Freight Shipping Solutions?</h2>
          <p className="text-zinc-400 mb-6">Get a free quote or browse available loads on our live load board.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold uppercase tracking-wide transition-colors">
              Get a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/load-board" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-zinc-900 px-6 py-3 font-bold uppercase tracking-wide transition-colors">
              View Load Board
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};


// ==================== SINGLE ARTICLE ====================
export const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then(r => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-3xl font-black text-zinc-900 mb-3">Article Not Found</h1>
        <p className="text-zinc-500 mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <Link to="/blog" className="text-red-600 font-bold hover:underline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  const keywords = article.keywords ? article.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];

  return (
    <div data-testid="blog-article-page">
      <Helmet>
        <title>{`${article.title} | Red Ball Express Blog`}</title>
        <meta name="description" content={article.excerpt || article.title} />
        {article.keywords ? <meta name="keywords" content={article.keywords} /> : null}
        <link rel="canonical" href={`${SITE_URL}/blog/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || article.title} />
        {article.featured_image ? <meta property="og:image" content={article.featured_image} /> : null}
      </Helmet>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.featured_image || "",
            "author": { "@type": "Person", "name": article.author_name || "Red Ball Express" },
            "publisher": { "@type": "Organization", "name": "Red Ball Express Logistics", "url": SITE_URL },
            "datePublished": article.published_at,
            "dateModified": article.updated_at,
            "mainEntityOfPage": `${SITE_URL}/blog/${article.slug}`,
            "keywords": article.keywords || "",
          })}
        </script>
      </Helmet>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" className="text-red-600 hover:text-red-700 text-sm font-medium mb-6 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        {article.category && (
          <span className="block text-xs font-bold uppercase text-red-600 tracking-wide mt-4">{article.category}</span>
        )}
        <h1 className="text-3xl sm:text-4xl font-black text-zinc-900 mt-2 mb-4 leading-tight">
          {article.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-8 pb-6 border-b border-zinc-200">
          {article.published_at && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(article.published_at)}
            </span>
          )}
          {article.author_name && (
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {article.author_name}
            </span>
          )}
        </div>

        {article.featured_image && (
          <img
            src={article.featured_image}
            alt={article.title}
            className="w-full aspect-video object-cover mb-10 border border-zinc-200"
          />
        )}

        <div
          className="prose prose-zinc prose-lg max-w-none prose-headings:font-black prose-a:text-red-600 prose-img:rounded-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          data-testid="article-content"
        />

        {keywords.length > 0 && (
          <div className="mt-10 pt-6 border-t border-zinc-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-zinc-400" />
              {keywords.map(k => (
                <span key={k} className="px-3 py-1 bg-zinc-100 text-zinc-600 text-xs font-medium">{k}</span>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};
